import { createFileRoute, useParams, useNavigate } from '@tanstack/react-router'
import { trpc } from '@/lib/trpc'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { ArrowRight } from 'lucide-react'
import { useWorkspacePermissions } from '@/hooks/useWorkspacePermissions'
import { ScreenataStepLayout } from '@/components/screenata/ScreenataStepLayout'
import { ConnectRepoCard } from '@/components/screenata/ConnectRepoCard'
import PageLoading from '@/components/PageLoading'
import ForbiddenError from '@/pages/errors/forbidden'

export const Route = createFileRoute(
  '/(protected)/workspaces/$workspaceId/connect-repo',
)({
  component: ConnectRepoPage,
})

function ConnectRepoPage() {
  const { workspaceId } = useParams({
    from: '/(protected)/workspaces/$workspaceId/connect-repo',
  })
  const { isAdmin, isLoading: isLoadingPermissions } =
    useWorkspacePermissions(workspaceId)
  const navigate = useNavigate()
  const qc = useQueryClient()

  const programQuery = useQuery({
    ...trpc.complianceProgram.getProgram.queryOptions(),
    enabled: !isLoadingPermissions && isAdmin,
  })
  const findingsQuery = useQuery({
    ...trpc.complianceProgram.getScanFindings.queryOptions(),
    enabled: !isLoadingPermissions && isAdmin,
  })

  const skipScanningMutation = useMutation(
    trpc.complianceProgram.skipScanning.mutationOptions(),
  )

  const program = programQuery.data
  const hasTerminalRun =
    !!findingsQuery.data?.runId && !!findingsQuery.data?.isTerminal

  const handleContinue = () => {
    navigate({
      to: '/workspaces/$workspaceId/integrations',
      params: { workspaceId },
    })
  }

  const handleSkip = () => {
    if (!program?.id) return
    skipScanningMutation.mutate(
      { programId: program.id },
      {
        onSuccess: () => {
          qc.invalidateQueries({
            queryKey: trpc.complianceProgram.getProgram.queryKey(),
          })
          navigate({
            to: '/workspaces/$workspaceId/connect-tools',
            params: { workspaceId },
          })
        },
      },
    )
  }

  const handleGoBack = () => {
    navigate({
      to: '/workspaces/$workspaceId/screenata',
      params: { workspaceId },
      search: { step: 99 },
    })
  }

  const footer = (
    <>
      {hasTerminalRun && (
        <button
          onClick={handleContinue}
          className="inline-flex items-center gap-2 rounded-md bg-foreground px-5 py-2.5 text-sm font-medium text-background transition-opacity hover:opacity-90"
        >
          Continue
          <ArrowRight className="h-4 w-4" />
        </button>
      )}
      {program && (
        <button
          onClick={handleSkip}
          disabled={skipScanningMutation.isPending}
          className="text-sm text-muted-foreground underline-offset-2 hover:underline"
        >
          {skipScanningMutation.isPending ? 'Skipping...' : 'Skip'}
        </button>
      )}
    </>
  )

  if (isLoadingPermissions) return <PageLoading />
  if (!isAdmin) return <ForbiddenError />

  return (
    <ScreenataStepLayout
      section="Source code"
      title="Connect GitHub"
      description="Connect your repositories so we can analyze your codebase for compliance-relevant infrastructure."
      footer={footer}
      onBack={handleGoBack}
      onNext={hasTerminalRun ? handleContinue : undefined}
    >
      <ConnectRepoCard workspaceId={workspaceId} />
    </ScreenataStepLayout>
  )
}
